<template>
  <v-row no-gutters>
    <v-col cols="12">
      <v-row no-gutters class="align-center">
        <v-col cols="auto">
          <v-subheader class="text-subtitle-2 font--text pl-0" v-text="$i18n.t('featureCard.watermeters')">
          </v-subheader>
        </v-col>
        <v-spacer> </v-spacer>
        <v-col cols="auto">
          <base-rounded-button
            icon="mdi-plus"
            size="x-small"
            color="success"
            @click="openWatermeterAddingDialog"
            :tooltip="$i18n.t('button.assignWatermeter')"
          />
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col>
          <div v-if="hasAnyWatermeters">
            <widget-partitions-watermeters-treeview v-bind="$props" @deleteAssignment="openDeleteAssignmentDialog">
            </widget-partitions-watermeters-treeview>
          </div>
          <v-subheader v-else class="justify-center">
            <span>
              {{ noAssignedWatermetersText }}
            </span>
          </v-subheader>
        </v-col>
      </v-row>
    </v-col>
    <main-input-dialog
      v-if="isAddingWatemeterLoaded"
      :is-visible.sync="isAddingWatemeterVisible"
      :loading="isAddingWatermeterLoading"
      :title-translation="dialogTitle"
      agree-text="button.assign"
      @agree="assignWatermeter"
      :inputs="inputs"
      dialog-width="40%"
    >
      <template #watermeterId.input="slot">
        <v-col cols="4">
          <data-input v-model="watermeterId" v-bind="slot" :rules="slot.rules"></data-input>
        </v-col>
      </template>
    </main-input-dialog>
    <main-dialog
      v-if="isDeleteDialogLoaded"
      :is-visible.sync="isDeleteDialogVisible"
      :title="$i18n.t('dialog.title.deleteWatermeterAssignment')"
      :body="deleteDialogBody"
      @agree="deleteWatermeterAssignmentHandler"
      :loading="isDeleteDialogLoading"
      agree-text="dialog.agreeDelete"
    ></main-dialog>
  </v-row>
</template>

<script>
import { get, call } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import WidgetPartitionsWatermetersTreeview from '@/components/WidgetPartitionsWatermetersTreeview';

export default {
  name: 'WidgetPartitionsWatermeters',
  components: {
    DataInput,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
    WidgetPartitionsWatermetersTreeview,
  },
  props: {
    partitionId: {
      type: [Number, String],
      required: true,
    },
    watermeters: {
      type: Array,
      default: () => [],
    },
    hasLegalization: {
      type: Boolean,
      default: false,
    },
    hasWaterUsage: {
      type: Boolean,
      default: false,
    },
    hasWatermeterKind: {
      type: Boolean,
      default: false,
    },
    hasParentWatemeter: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isAddingWatemeterLoaded: false,
    isAddingWatemeterVisible: false,
    isAddingWatermeterLoading: false,
    watermetersIds: [],
    watermeterId: undefined,
    isDeleteDialogLoaded: false,
    isDeleteDialogVisible: false,
    isDeleteDialogLoading: false,
    deletedWatermeter: undefined,
    getWatermetersLoading: true,
  }),
  computed: {
    watermeterSerialNumberAttributeName: get(
      'admin/modulesMapping@billing.watermeters_datasource_attributes_mapping.watermeter_serial_number_attribute_name'
    ),
    dialogTitle() {
      return this.$i18n.t('dialog.title.assignWatermeter', { id: this.partitionId });
    },
    inputs() {
      return [
        {
          name: 'watermeterId',
          translationPath: 'dialog.body.watermeter',
          rules: [v => !!v || 'rules.required'],
          leftCol: 4,
          clearable: true,
          dataType: {
            name: 'autocomplete',
          },
          items: this.watermetersIds,
          itemText: 'text',
          itemValue: 'billingId',
          loading: this.getWatermetersLoading,
        },
      ];
    },
    hasAnyWatermeters() {
      return this.watermeters.length > 0;
    },
    noAssignedWatermetersText() {
      return this.$i18n.t('featureCard.noAssignedWatermeters');
    },
    deleteDialogBody() {
      return this.$i18n.t('dialog.body.deleteWatermeterAssignment', {
        id: `${this.deletedWatermeter.watermeter_serial_number} (${this.deletedWatermeter.billing_id})`,
      });
    },
  },
  methods: {
    getUnassignedWatermeters: call('billings/getUnassignedWatermeters'),
    asignPartitionWatermeter: call('billings/asignPartitionWatermeter'),
    deletePartitionWatermeter: call('billings/deletePartitionWatermeter'),
    openDeleteAssignmentDialog(item) {
      this.deletedWatermeter = item;
      this.isDeleteDialogLoaded = true;
      this.isDeleteDialogVisible = true;
    },
    async deleteWatermeterAssignmentHandler() {
      try {
        this.isDeleteDialogLoading = true;
        await this.deletePartitionWatermeter(this.deletedWatermeter.watermeter_id);
        this.isDeleteDialogVisible = false;
        this.$emit('refetchPartitionWatermeters');
      } catch (error) {
        console.log(error);
      }
      this.isDeleteDialogLoading = false;
    },
    openWatermeterAddingDialog() {
      this.getWatermeters();
      this.isAddingWatemeterLoaded = true;
      this.isAddingWatemeterVisible = true;
    },
    async assignWatermeter() {
      try {
        this.isAddingWatermeterLoading = true;
        const { watermeterId, partitionId } = this;
        await this.asignPartitionWatermeter({
          watermeterId,
          partitionId,
        });
        this.isAddingWatemeterVisible = false;
        this.$emit('refetchPartitionWatermeters');
      } catch (error) {
        console.log(error);
      }
      this.isAddingWatermeterLoading = false;
    },
    async getWatermeters() {
      try {
        this.getWatermetersLoading = true;
        const r = await this.getUnassignedWatermeters();
        const data = r.data.data;
        const featuresBillingIds = data.map(feature => {
          return { billingId: feature.billing_id, text: `${feature.watermeter_serial_number} (${feature.billing_id})` };
        });
        this.watermetersIds = featuresBillingIds;
        this.getWatermetersLoading = false;
      } catch (error) {
        console.log(error);
        this.getWatermetersLoading = false;
      }
    },

    hydrantFetched(feature) {
      this.currentHydrantFeature = feature;
    },
    openInspection(id) {
      this.currentInspectionId = id;
      this.isHydrantInspectionVisible = true;
      this.isHydrantInspectionLoaded = true;
    },
    prepareHydrantInspectionsFilter() {
      return {
        $EQUAL: { [`${this.hydrantsInspectionsDataSourceName}.hydrant_id`]: this.currentHydrantId },
      };
    },
    toggleInspections() {
      this.inspectionsVisible = !this.inspectionsVisible;
    },
    toggleHydrantInspectionAdding() {
      this.openInspection('new');
    },
    async init() {
      if (!this.isHydrantInspectionsVisible) {
        return;
      }
      this.isHydrantInspectionVisible = false;
      const payload = {
        features_filter: this.prepareHydrantInspectionsFilter(),
        sorting: [
          {
            attribute_name: 'date_carried_out',
            direction: 'desc',
          },
        ],
        hydrantId: this.currentHydrantId,
      };
      await this.getHydrantsInspections(payload);
      this.$refs.featureCard.onLidFidChanged(this.currentHydrantId, this.currentHydrantId);
    },
  },
};
</script>

<style lang="scss" scoped></style>
